import { useEffect, useRef } from 'react';

/** Handle touch and left-button mouse swipes, including release outside the element. */
export const useSwipe = (onSwipeLeft, onSwipeRight, threshold = 50) => {
  const touchStartX = useRef(null);
  const mouseStartX = useRef(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    const handleMouseUp = (event) => {
      const start = mouseStartX.current;
      mouseStartX.current = null;
      if (start === null) return;

      const distance = start - event.clientX;
      if (Math.abs(distance) >= threshold) {
        suppressClick.current = true;
        if (distance > 0) onSwipeLeft?.();
        else onSwipeRight?.();
      }
    };
    const cancelDrag = () => { mouseStartX.current = null; };

    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', cancelDrag);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', cancelDrag);
    };
  }, [onSwipeLeft, onSwipeRight, threshold]);

  return {
    onTouchStart: (event) => {
      suppressClick.current = false;
      touchStartX.current = event.touches[0].clientX;
    },
    onTouchEnd: (event) => {
      const start = touchStartX.current;
      touchStartX.current = null;
      if (start === null) return;

      const distance = start - event.changedTouches[0].clientX;
      if (Math.abs(distance) >= threshold) {
        suppressClick.current = true;
        if (distance > 0) onSwipeLeft?.();
        else onSwipeRight?.();
      }
    },
    onTouchCancel: () => { touchStartX.current = null; },
    onDragStart: (event) => event.preventDefault(),
    onClickCapture: (event) => {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      if (event.detail === 0) return; // Keyboard activation is never a drag click.
      event.preventDefault();
      event.stopPropagation();
    },
    onMouseDown: (event) => {
      suppressClick.current = false;
      if (event.button === 0) mouseStartX.current = event.clientX;
    },
  };
};
